import { useEffect, useState } from 'react';
import { useLeasingUrlParams } from '../../../hooks/useLeasingUrlParams';
import { isBefore, isFuture } from 'date-fns';

import { Button, ButtonGroup, Heading, TextButton } from '@zillow/constellation-10';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import {
  useTerminateLeaseMutation,
  useTerminationReasonsQuery,
  useWebAnalyticsForListingQuery,
} from '../../../utils/queries';

import CancellationConfirmation from './OpenModalCancelActive/CancellationConfirmation';
import CancellationPrompt from './OpenModalCancelActive/CancellationPrompt';
import LeaseModal from '../../Containers/LeaseModal';
import type { LeaseActionProps } from '../../../models/Types';
import { getUTCTimeFromDateString } from '../../../utils/dates';
import { emitCancelActiveConfirmEvent, emitCancelActiveOpenEvent } from '../../../../analytics/events';
import type { TerminationReason } from '@zg-rentals/ts-leasing-client';

type Props = LeaseActionProps;

export function OpenModalCancelActive({ terminateActiveAction, lease }: Props): JSX.Element {
  const { user } = useRentalManagerUser();
  const { propertyId } = useLeasingUrlParams();
  const [cancellationReason, setCancellationReason] = useState<TerminationReason>();
  const [canceledDate, setCanceledDate] = useState<string>('');
  const [isCanceled, setIsCanceled] = useState(false);
  const [isCanceledDateInError, setIsCanceledDateInError] = useState(false);
  const [canceledDateError, setCanceledDateError] = useState('');

  const { data, isFetching } = useTerminationReasonsQuery();
  const terminateLeaseMutation = useTerminateLeaseMutation();
  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;

  const activeCancelationReasons = data?.reasons || [];
  const isLoading = isFetching || terminateLeaseMutation.isPending;

  useEffect(() => {
    emitCancelActiveOpenEvent({ user, listing: zrm_listing_info, lease: { ...lease, id: Number(lease.id) } });
  }, [user, zrm_listing_info, lease]);

  async function onCancelSigningClick(): Promise<void> {
    await terminateLeaseMutation.mutateAsync({
      postData: {
        leaseId: lease.id as unknown as number,
        terminationReason: cancellationReason,
        endDate: Number(getUTCTimeFromDateString(canceledDate)),
      },
    });

    setIsCanceled(true);
    emitCancelActiveConfirmEvent({ user, listing: zrm_listing_info, lease: { ...lease, id: Number(lease.id) } });
  }

  function onOkClick(): void {
    terminateActiveAction();
  }

  function isCanceledDateOutOfRange(date: Date) {
    return isCanceledDateInFuture(date) || isCanceledDateBeforeLeaseCreationDate(date);
  }

  function isCanceledDateInFuture(date: Date) {
    return isFuture(date);
  }

  function isCanceledDateBeforeLeaseCreationDate(date: Date) {
    // Take creation date ms and convert to date
    const creationDateLocal = new Date(Number(lease.creationDate));
    // Convert to midnight of the current date, so everything before it is the previous date, and will allow for all times in the lease creation date
    const creationDateMidnight = new Date(
      creationDateLocal.getUTCFullYear(),
      creationDateLocal.getUTCMonth(),
      creationDateLocal.getUTCDate(),
    );
    return isBefore(date, creationDateMidnight);
  }

  function onCanceledDateChange(date: string) {
    const canceledDateInput = new Date(date);
    if (isNaN(Date.parse(date))) {
      setIsCanceledDateInError(true);
      setCanceledDateError('Enter a valid date');
    } else if (isCanceledDateInFuture(canceledDateInput)) {
      setIsCanceledDateInError(true);
      setCanceledDateError('Date cannot be in the future');
    } else if (isCanceledDateBeforeLeaseCreationDate(canceledDateInput)) {
      setIsCanceledDateInError(true);
      setCanceledDateError('Date cannot be before the lease creation date');
    } else {
      setIsCanceledDateInError(false);
      setCanceledDateError('');
    }
    setCanceledDate(date);
  }

  return (
    <LeaseModal
      header={<Heading level={1}>{!isCanceled ? 'Cancel active lease?' : 'Lease canceled'}</Heading>}
      isOpen
      onClose={terminateActiveAction}
      body={
        !isCanceled ? (
          <CancellationPrompt
            activeCancelationReasons={activeCancelationReasons}
            canceledDate={canceledDate}
            cancellationReason={cancellationReason}
            onCanceledDateChange={onCanceledDateChange}
            onCancellationReasonChange={setCancellationReason}
            lease={lease}
            isCanceledDateOutOfRange={isCanceledDateOutOfRange}
            isCanceledDateInError={isCanceledDateInError}
            canceledDateError={canceledDateError}
            isLoadingReasons={isFetching}
          />
        ) : (
          <CancellationConfirmation
            cancellationReason={activeCancelationReasons.find(({ name }) => name === cancellationReason).label}
          />
        )
      }
      footer={
        !isCanceled ? (
          <ButtonGroup aria-label="cancel signing">
            <TextButton tone="brand" data-test="go-back" onClick={terminateActiveAction} disabled={isLoading}>
              Keep lease active
            </TextButton>
            <Button
              data-test="cancel-signing"
              disabled={!cancellationReason || isCanceledDateInError || isLoading || !canceledDate}
              tone="brand"
              emphasis="filled"
              onClick={onCancelSigningClick}
            >
              Cancel lease
            </Button>
          </ButtonGroup>
        ) : (
          <ButtonGroup aria-label="lease canceled">
            <TextButton tone="brand" data-test="goto-dashboard-button" onClick={onOkClick}>
              OK
            </TextButton>
          </ButtonGroup>
        )
      }
    />
  );
}

export default OpenModalCancelActive;
